import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pie } from '@ant-design/plots';
import IconNoData from '../../../assets/Images/icons/icon-no-data';
import { Spin } from 'antd';

const RequestTypePie = memo(function RequestTypePie({
    requestInfo,
    total,
    isLoadingPie,
}) {
    const { t } = useTranslation();

    const config = useMemo(() => {
        return {
            data: requestInfo?.reverse(),
            angleField: 'value',
            colorField: 'requestTypeShortName',
            color: ({ requestTypeShortName }) => {
                switch (requestTypeShortName.toLowerCase()) {
                    case 'right to withdraw consent': //เพิกถอน
                        return '#FEDB39';
                    case 'right to be access': //ขอดู/ขอสำเนาข้อมูล
                        return '#FF8C8C';
                    case 'right to be inform': //ขอทราบการใช้งาน
                        return '#1CC5DC';
                    case 'right to object': // คัดค้านการใช้งาน
                        return '#C3E5AE';
                    case 'right to erasure': // ลบข้อมูล
                        return '#FCA652';
                    case 'right to be restrict processing': // ระงับการใช้ข้อมูล
                        return '#B2A4FF';
                    case 'right to rectification': // แก้ไขข้อมูล
                        return '#FFCCD2';
                    case 'right to data portability': // ย้ายข้อมูล
                        return '#FFD07F';
                    case 'right to complaint': // ร้องเรียน
                        return '#40A8C4';
                    case 'เพิกถอน': //เพิกถอน
                        return '#FEDB39';
                    case 'ขอดู/ขอสำเนาข้อมูล': //ขอดู/ขอสำเนาข้อมูล
                        return '#FF8C8C';
                    case 'ขอทราบการใช้งาน': //ขอทราบการใช้งาน
                        return '#1CC5DC';
                    case 'คัดค้านการใช้งาน': // คัดค้านการใช้งาน
                        return '#C3E5AE';
                    case 'ลบข้อมูล': // ลบข้อมูล
                        return '#FCA652';
                    case 'ระงับการใช้ข้อมูล': // ระงับการใช้ข้อมูล
                        return '#B2A4FF';
                    case 'แก้ไขข้อมูล': // แก้ไขข้อมูล
                        return '#FFCCD2';
                    case 'ย้ายข้อมูล': // ย้ายข้อมูล
                        return '#FFD07F';
                    case 'ร้องเรียน': // ร้องเรียน
                        return '#40A8C4';
                    default:
                        return '#FFFFF'; //อื่นๆ
                }
            },
            appendPadding: 10,
            radius: 1,
            width: 300,
            height: 300,
            innerRadius: 0.6,
            label: false,
            legend: false,
            interactions: [
                {
                    type: 'element-active',
                },
            ],
            pieStyle: {
                cursor: 'pointer',
            },
            statistic: {
                title: false,
                content: {
                    style: {
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#505D6F',
                        whiteSpace: 'pre-wrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    content: `${t('Dashboard360.RequestType')}\n\n${total}`,
                },
            },
            tooltip: {
                customContent: (title, data) => {
                    return (
                        <div className="p-4 flex space-x-4">
                            <div>
                                <svg
                                    width="12"
                                    height="13"
                                    viewBox="0 0 12 13"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="6"
                                        cy="6.5"
                                        r="6"
                                        fill={`${data[0]?.color}`}
                                    />
                                </svg>
                            </div>
                            <div className="font-semibold">{`${title}`}</div>
                            <div className="font-semibold">{`${data[0]?.value}`}</div>
                        </div>
                    );
                },
            },
        };
    }, [requestInfo, t, total]);

    return (
        <Spin spinning={isLoadingPie}>
            {requestInfo?.length <= 0 ? (
                <>
                    <div className="grid grid-cols-5 flex items-center">
                        <div className="col-span-3 mt-4">
                            <div className="flex space-x-2 items-center pb-4">
                                <span className="font-semibold text-lg">
                                    {t('Dashboard360.RequestType')}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="text-center text-sm space-y-2 my-32">
                        <IconNoData />
                        <p className="text-[#65707A] text-[14px]">
                            {t('common.noData')}
                        </p>
                    </div>
                </>
            ) : (
                <>
                    <div className="flex space-x-2 items-center pb-4">
                        <span className="font-semibold text-lg">
                            {t('Dashboard360.PieTitle')}
                        </span>
                    </div>

                    <div className="grid grid-cols-5 flex items-center">
                        <div
                            className="col-span-3 mt-4"
                            style={{ minHeight: '300px', maxHeight: '300px' }}
                        >
                            <Pie {...config} className="cursor-pointer" />
                        </div>
                        <div className="col-span-2 ">
                            {/* ขอทราบการใช้งาน */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#1CC5DC"
                                        />
                                    </svg>
                                    <div className="pl-2 text-sm">
                                        {t('Dashboard360.Inform')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to be inform'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to be inform'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to be inform'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>

                            {/* แก้ไขข้อมูล */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#FFCCD2"
                                        />
                                    </svg>
                                    <div className="pl-2">
                                        {t('Dashboard360.Rectification')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to rectification'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to rectification'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to rectification'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>

                            {/* เพิกถอน */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#FEDB39"
                                        />
                                    </svg>
                                    <div className="pl-2">
                                        {t('Dashboard360.WithdrawConsent')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to withdraw consent'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to withdraw consent'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to withdraw consent'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>

                            {/* ระงับการใช้ข้อมูล */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#B2A4FF"
                                        />
                                    </svg>
                                    <div className="pl-2">
                                        {t('Dashboard360.RestrictProcessing')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to be restrict processing'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to be restrict processing'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to be restrict processing'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>

                            {/* ขอดู/ขอสำเนาข้อมูล */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#FF8C8C"
                                        />
                                    </svg>
                                    <div className="pl-2">
                                        {t('Dashboard360.Access')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to be access'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to be access'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to be access'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>

                            {/* ย้ายข้อมูล */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#FFD07F"
                                        />
                                    </svg>
                                    <div className="pl-2">
                                        {t('Dashboard360.DataPortability')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to data portability'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to data portability'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to data portability'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>

                            {/* คัดค้านการใช้งาน */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#C3E5AE"
                                        />
                                    </svg>
                                    <div className="pl-2">
                                        {t('Dashboard360.Objection')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to object'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to object'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to object'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>

                            {/* ลบข้อมูล */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#FCA652"
                                        />
                                    </svg>
                                    <div className="pl-2">
                                        {t('Dashboard360.Erasure')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to erasure'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to erasure'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to erasure'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>

                            {/* ร้องเรียน */}
                            <div className="flex justify-between py-1">
                                <div className="flex items-center">
                                    <svg
                                        width="12"
                                        height="13"
                                        viewBox="0 0 12 13"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <circle
                                            cx="6"
                                            cy="6.5"
                                            r="6"
                                            fill="#40A8C4"
                                        />
                                    </svg>
                                    <div className="pl-2">
                                        {t('Dashboard360.Complaint')}
                                    </div>
                                </div>
                                <div className="text-sm font-medium text-gray-500">
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to complaint'
                                    )?.value || 0}{' '}
                                    (
                                    {requestInfo?.find(
                                        (e) =>
                                            e.requestTypeNameEn?.toLowerCase() ===
                                            'right to complaint'
                                    )?.value
                                        ? (
                                              (Number(
                                                  requestInfo?.find(
                                                      (e) =>
                                                          e.requestTypeNameEn?.toLowerCase() ===
                                                          'right to complaint'
                                                  )?.value
                                              ) /
                                                  Number(total)) *
                                              100
                                          ).toFixed(2)
                                        : '00.00'}{' '}
                                    %)
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Spin>
    );
});

export default RequestTypePie;
